/* eslint-disable import/order */
/* eslint-disable camelcase */
import { useState, useEffect } from 'react';
// @mui
import { useTheme, alpha } from '@mui/material/styles';
import {
  Card,
  Grid,
  Container,
  Typography,
  Box,
  CircularProgress,
  Divider,
  Button,
} from '@mui/material';
// routes
import axios, { getErrorMessage } from 'utils/axios';
import useIsMountedRef from 'hooks/useIsMountedRef';
// hooks
import useSettings from '../../hooks/useSettings';
import { useSnackbar } from 'notistack';
// components
import Page from '../../components/Page';
import HeaderBreadcrumbs from '../../components/HeaderBreadcrumbs';
import CRUD from 'utils/Crud';
import { PATH_DASHBOARD } from 'routes/paths';
import Iconify from 'components/Iconify';


export default function CruiseMessageOverview() {
  const { themeStretch } = useSettings();
  const isMountedRef = useIsMountedRef();
  const [state, setState] = useState({
    loading: false,
    error: null,
  });
  const [stats, setStats] = useState(null);
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();

  useEffect(() => {
    getInitData();
  }, []);

  async function getInitData() {
    setState({ loading: true, error: null });
    try {
      const response = await axios.get(CRUD.cruise.overview); // Your getCruiseTableAnalysis endpoint

      if (isMountedRef.current) {
        setStats(response.data);
      }
      setState({ loading: false, error: null });
    } catch (error) {
      setState({ loading: false, error: getErrorMessage(error) });
      enqueueSnackbar(getErrorMessage(error), { variant: 'error' });
    }
  }

  // Helper renderers for grouped data
  const renderCategoryRatingStats = () => {
    // Group by category with ratings inside
    const grouped = {};
    stats.category_rating_stats.forEach(({ category, rating, count }) => {
      if (!grouped[category]) grouped[category] = [];
      grouped[category].push({ rating, count });
    });
    return (
      <Grid container spacing={2}>
        {Object.entries(grouped).map(([category, ratings]) => (
          <Grid item xs={12} sm={6} md={4} key={`cat-rating-${category}`}>
            <Card
              variant="outlined"
              sx={{
                p: 2,
                bgcolor: theme.palette.background.paper,
                height: '100%',
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary }}>
                Category: {category}
              </Typography>
              {ratings.map(({ rating, count }) => (
                <Typography key={`${category}-${rating}`} sx={{ color: theme.palette.text.secondary, ml: 1 }}>
                  Rating {rating}: {count.toLocaleString()}
                </Typography>
              ))}
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderStatusCategoryStats = () => {
    // Group by status -> categories
    const grouped = {};
    stats.status_category_stats.forEach(({ status, category, count }) => {
      if (!grouped[status]) grouped[status] = [];
      grouped[status].push({ category, count });
    });

    // Define calm colors for statuses - adapt to light/dark mode
    const statusColors = {
      approved: {
        bg: theme.palette.mode === 'light' ? alpha(theme.palette.success.light, 0.15) : alpha(theme.palette.success.dark, 0.3),
        text: theme.palette.success.main,
      },
      pending: {
        bg: theme.palette.mode === 'light' ? alpha(theme.palette.warning.light, 0.15) : alpha(theme.palette.warning.dark, 0.3),
        text: theme.palette.warning.dark,
      },
      default: {
        bg: theme.palette.background.paper,
        text: theme.palette.text.primary,
      },
    };

    return (
      <Grid container spacing={2} sx={{ mt: 2 }}>
        {Object.entries(grouped).map(([status, categories]) => {
          const colors = statusColors[status.toLowerCase()] || statusColors.default;
          return (
            <Grid item xs={12} sm={6} md={4} key={`status-cat-${status}`}>
              <Card
                variant="outlined"
                sx={{
                  p: 2,
                  bgcolor: colors.bg,
                  height: '100%',
                }}
              >
                <Typography variant="h6" gutterBottom sx={{ color: colors.text }}>
                  Status: {status.charAt(0).toUpperCase() + status.slice(1)}
                </Typography>
                {categories.map(({ category, count }) => (
                  <Typography key={`${status}-${category}`} sx={{ color: theme.palette.text.secondary, ml: 1 }}>
                    {category.charAt(0).toUpperCase() + category.slice(1)}: {count.toLocaleString()}
                  </Typography>
                ))}
              </Card>
            </Grid>
          );
        })}
      </Grid>
    );
  };

  const renderDistinctReviewedPerCategory = () => {
    return (
      <Grid container spacing={2} sx={{ mt: 2 }}>
        {stats.distinct_reviewed_per_category.map(({ category, reviewed_cruises }) => (
          <Grid item xs={12} sm={6} md={4} key={`distinct-reviewed-${category}`}>
            <Card variant="outlined" sx={{ p: 2, bgcolor: theme.palette.background.paper }}>
              <Typography variant="h6" gutterBottom sx={{ color: theme.palette.text.primary }}>
                Distinct Reviewed Cruises
              </Typography>
              <Typography sx={{ color: theme.palette.text.secondary }}>
                Category: {category}
              </Typography>
              <Typography variant="h4" sx={{ mt: 1, fontWeight: 'bold', color: theme.palette.primary.main }}>
                {reviewed_cruises.toLocaleString()}
              </Typography>
            </Card>
          </Grid>
        ))}
      </Grid>
    );
  };

  const renderSingleStatCard = (title, value, sx = {}) => (
    <Card
      variant="outlined"
      sx={{
        p: 3,
        textAlign: 'center',
        bgcolor: theme.palette.background.paper,
        height: '100%',
        ...sx,
      }}
    >
      <Typography variant="subtitle1" sx={{ color: theme.palette.text.secondary }}>
        {title}
      </Typography>
      <Typography variant="h3" sx={{ fontWeight: 'bold', color: theme.palette.primary.main }}>
        {value.toLocaleString()}
      </Typography>
    </Card>
  );

  return (
    <Page title="Manage Cruise">
      <Container maxWidth={themeStretch ? false : 'lg'}>
        <HeaderBreadcrumbs
          heading="Cruise Message Overview"
          links={[
            { name: 'Cruise center overview', href: PATH_DASHBOARD.cruiseCenter.root },
            { name: 'Overview' },
          ]}
          sx={{ mb: 4 }}
          action={
            <Button
              onClick={() => getInitData()}
              startIcon={<Iconify icon="mdi:refresh" />}
              disabled={state.loading}
              variant="outlined"
              size="small"
            >
              Refresh
            </Button>
          }
        />

        {state.loading && (
          <Box display="flex" justifyContent="center" my={10}>
            <CircularProgress />
          </Box>
        )}

        {!state.loading && state.error && (
          <Card sx={{ p: 4, bgcolor: theme.palette.error.light }}>
            <Typography color="error" align="center">
              Error loading data: {state.error}
            </Typography>
          </Card>
        )}

        {!state.loading && stats && (
          <Box>
            <Grid container spacing={3} mb={3}>
              {/* Single number stats */}
              <Grid item xs={12} sm={6} md={3}>
                {renderSingleStatCard('Total Cruises (non-deleted)', stats.total_cruises)}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderSingleStatCard('Deleted Cruises', stats.deleted_cruises)}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderSingleStatCard('Reviewed & Approved', stats.reviewed_approved_count)}
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                {renderSingleStatCard('Pending Cruises', stats.pending_count)}
              </Grid>
            </Grid>

            <Divider sx={{ mb: 3 }} />

            <Typography variant="h5" gutterBottom>
              Category & Rating Distribution
            </Typography>
            {stats.category_rating_stats.length > 0 ? (
              renderCategoryRatingStats()
            ) : (
              <Typography variant="body2" color="textSecondary">
                No data available.
              </Typography>
            )}

            <Divider sx={{ my: 4 }} />

            <Typography variant="h5" gutterBottom>
              Status & Category Distribution
            </Typography>
            {stats.status_category_stats.length > 0 ? (
              renderStatusCategoryStats()
            ) : (
              <Typography variant="body2" color="textSecondary">
                No data available.
              </Typography>
            )}

            <Divider sx={{ my: 4 }} />

            <Typography variant="h5" gutterBottom>
              Distinct Reviewed Cruises Per Category
            </Typography>
            {stats.distinct_reviewed_per_category.length > 0 ? (
              renderDistinctReviewedPerCategory()
            ) : (
              <Typography variant="body2" color="textSecondary">
                No data available.
              </Typography>
            )}
          </Box>
        )}
      </Container>
    </Page>
  );
}
